import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';
/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;
/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];
  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;
  #submenuResizeObserver = null;
  #heightFrame = 0;
  #closeTimer;
  #cancelScheduledClose = () => {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = undefined;
  };
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("pointerover", this.#cancelScheduledClose);
    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("pointerover", this.#cancelScheduledClose);
    this.#cancelScheduledClose();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
  }
  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);
  #overflowSubmenuListener = () => {
    this.#deactivate();
  };
  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };
  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;
  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };
  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;
    this.#lastPointer = { x: event.clientX, y: event.clientY };
    activeLink.dataset.safetyBox = 'false';
    if (this.#isPointerWithinMenu()) {
      this.#cancelScheduledClose();
    } else if (this.#closeTimer === undefined) {
      this.deactivate({ target: activeLink, relatedTarget: null, type: 'pointerleave' });
    }
  };
  #isPointerWithinMenu() {
    const link = this.#state.activeItem;
    const submenu = findSubmenu(link);
    if (!link || !submenu) return false;
    if (link.matches(':hover') || submenu.matches(':hover')) return true;
    const { x, y } = this.#lastPointer;
    const a = link.getBoundingClientRect();
    const b = submenu.getBoundingClientRect();
    const inner = submenu.querySelector('.menu-list__submenu-inner');
    const contentTop = inner?.getBoundingClientRect().top ?? b.top;
    const bridgeBottom = Math.max(a.bottom, b.top, contentTop);
    // A geometric bridge, not a clickable overlay: slow movement is safe too.
    return y >= a.bottom && y <= bridgeBottom &&
      x >= a.left - 24 && x <= a.right + 24;
  }
  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }
  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }
    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }
  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }
  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }
  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }
  /**
   * Find the first overflowing menu item shown by the More trigger.
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }
  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }
  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#cancelScheduledClose();
    if (!(event.target instanceof Element) || !this.headerComponent) return;
    const isMoreTrigger = event.target.slot === 'more';
    const item = findMenuItem(event.target);
    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;
    if (!item || item == this.#state.activeItem) return;
    const isDefaultSlot = event.target.slot === '';
    this.dataset.overflowExpanded = (!isDefaultSlot).toString();
    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;
    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }
    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== previouslyActiveItem) {
      previouslyActiveOverflowItem.ariaExpanded = 'false';
    }
    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    if (overflowItem && overflowItem !== item) {
      overflowItem.ariaExpanded = 'true';
    }
    const overflowItemSubmenu = isMoreTrigger ? findSubmenu(overflowItem) : null;
    let submenu = findSubmenu(item) || (overflowItemSubmenu ? this.overflowMenu : null);
    const hasSubmenu = Boolean(submenu);
    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';
      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();
      // Keep the height correct throughout the open state, including hydration,
      // late font loading and image loading.
      const updateHeight = () => {
        cancelAnimationFrame(this.#heightFrame);
        this.#heightFrame = requestAnimationFrame(() => {
          if (this.#state.activeItem === item) this.#syncSubmenuHeight(submenu, isDefaultSlot);
        });
      };
      this.#submenuMutationObserver = new MutationObserver(updateHeight);
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] });
      if (typeof ResizeObserver !== 'undefined') {
        this.#submenuResizeObserver = new ResizeObserver(updateHeight);
        this.#submenuResizeObserver.observe(submenu.querySelector('.menu-list__submenu-inner') || submenu);
        // The inner wrapper can keep a fixed height while the list grows after
        // web fonts load. Observe the natural content height as well.
        for (const content of submenu.querySelectorAll('.mega-menu__grid, .mega-menu__list')) {
          this.#submenuResizeObserver.observe(content);
        }
      }
    }
    this.#syncSubmenuHeight(submenu, isDefaultSlot);
    if (submenu && document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (this.#state.activeItem === item && this.isConnected) {
          this.#syncSubmenuHeight(findSubmenu(item) || submenu, isDefaultSlot);
        }
      });
    }
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
    // Recheck after layout and any pending header hydration.
    if (submenu) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.#state.activeItem === item) {
            this.#syncSubmenuHeight(submenu, isDefaultSlot);
          }
        });
      });
    }
  };
  /**
   * Measure the open submenu after content-visibility is applied.
   * @param {HTMLElement | null} submenu
   * @param {boolean} isDefaultSlot
   */
  #syncSubmenuHeight(submenu, isDefaultSlot) {
    if (!this.headerComponent) return;
    let finalHeight = 0;
    if (submenu) {
      const inner = submenu.querySelector('.menu-list__submenu-inner');
      if (inner instanceof HTMLElement) {
        // Only measure rendered content, never the outer intrinsic placeholder.
        finalHeight = Math.max(inner.offsetHeight, inner.scrollHeight);
        const innerRect = inner.getBoundingClientRect();
        const innerStyle = window.getComputedStyle?.(inner);
        const paddingBottom = parseFloat(innerStyle?.paddingBottom || '0') || 0;
        for (const content of inner.querySelectorAll('.mega-menu__grid, .mega-menu__list')) {
          const contentRect = content.getBoundingClientRect();
          finalHeight = Math.max(finalHeight, Math.ceil(contentRect.bottom - innerRect.top + paddingBottom));
        }
        // Tall menus scroll inside the wrapper instead of enlarging the underlay.
        const maxHeight = parseFloat(innerStyle?.maxHeight || '');
        if (Number.isFinite(maxHeight)) finalHeight = Math.min(finalHeight, maxHeight);
      } else {
        finalHeight = submenu.offsetHeight;
      }
    }
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (submenu) {
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight, finalHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }
    if (!submenu) {
      finalHeight = 0;
    }
    finalHeight = Math.ceil(finalHeight);
    const headerVisibleHeight = this.#getHeaderVisibleHeight();
    if (this.headerComponent.style.getPropertyValue('--submenu-height') !== `${finalHeight}px`) {
      this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    }
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
  }
  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;
    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));
    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }
    if (event.type === 'blur') {
      this.#deactivate();
      return;
    }
    // Allow the pointer to cross the gap while the submenu is expanding.
    this.#cancelScheduledClose();
    const activeItem = this.#state.activeItem;
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = undefined;
      if (this.#state.activeItem !== activeItem) return;
      const activeSubmenu = findSubmenu(activeItem);
      if (activeItem?.matches(':hover') || activeSubmenu?.matches(':hover') || this.#isPointerWithinMenu()) return;
      this.#deactivate();
    }, 240);
  }
  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    this.#cancelScheduledClose();
    if (!item || item != this.#state.activeItem) return;
    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    const submenu = findSubmenu(item) || (item.closest('[slot="more"]') ? this.overflowMenu : null);
    const activeOverflowItem = this.#state.activeOverflowItem;
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);
    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    if (activeOverflowItem && activeOverflowItem !== item) {
      activeOverflowItem.ariaExpanded = 'false';
    }
    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };
  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;
    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };
    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }
  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;
    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }
  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;
    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;
    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }
  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
  #cleanupMutationObserver() {
    cancelAnimationFrame(this.#heightFrame);
    this.#heightFrame = 0;
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}
if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}
/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  return element?.querySelector('[ref="menuitem"]');
}
/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
